import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import React, { useEffect, useState } from 'react';

import { listPreviousMonthlyEditions } from 'services/firebase/monthlyEditionService';
import type { MonthlyEdition } from 'utils/types/MonthlyEdition';

const formatMonth = (month: string) => {
  const formatted = dayjs(`${month}-01`)
    .locale('pt-br')
    .format('MMMM [de] YYYY');
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const PreviousBeers = () => {
  const [editions, setEditions] = useState<MonthlyEdition[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    listPreviousMonthlyEditions()
      .then(data => {
        if (!cancelled) setEditions(data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ bgcolor: '#f7f2ec', minHeight: '60vh', py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <Typography
            component="h1"
            variant="h3"
            sx={{ fontWeight: 800, color: '#2c2118', mb: 2 }}
          >
            Cervejas anteriores
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 680, mx: 'auto' }}
          >
            Relembre os rótulos que já fizeram parte das seleções mensais do
            Clube da 42.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : editions.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 8 }}>
            Ainda não há edições anteriores disponíveis.
          </Typography>
        ) : (
          editions.map((edition, editionIndex) => (
            <Accordion
              key={edition.month}
              defaultExpanded={editionIndex === 0}
              disableGutters
              sx={{
                mb: 2,
                borderRadius: '16px !important',
                overflow: 'hidden',
                border: '1px solid #e8ddd1',
                '&::before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box>
                  <Typography
                    component="h2"
                    variant="h5"
                    sx={{ fontWeight: 700, color: '#2c2118' }}
                  >
                    {edition.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatMonth(edition.month)} · {edition.beers.length}{' '}
                    {edition.beers.length === 1 ? 'cerveja' : 'cervejas'}
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: { xs: 2, md: 3 }, pt: 0 }}>
                <Grid container spacing={3}>
                  {edition.beers.map((beer, beerIndex) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={`${beer.name}-${beerIndex}`}
                    >
                      <Card
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          boxShadow: '0 8px 24px rgba(44, 33, 24, 0.08)',
                        }}
                      >
                        {beer.image ? (
                          <CardMedia
                            component="img"
                            height="260"
                            image={beer.image}
                            alt={beer.name}
                            sx={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 260,
                              display: 'grid',
                              placeItems: 'center',
                              bgcolor: '#eee7df',
                            }}
                          >
                            <Typography color="text.secondary">
                              Imagem indisponível
                            </Typography>
                          </Box>
                        )}
                        <CardContent>
                          <Typography
                            component="h3"
                            variant="h6"
                            sx={{ fontWeight: 700, mb: 0.5 }}
                          >
                            {beer.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {beer.brewery}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1,
                              mb: beer.description ? 2 : 0,
                            }}
                          >
                            {beer.style && (
                              <Chip size="small" label={beer.style} />
                            )}
                            {beer.abv && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={beer.abv}
                              />
                            )}
                            {beer.ibu && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${beer.ibu} IBU`}
                              />
                            )}
                          </Box>
                          {beer.description && (
                            <Typography variant="body2">
                              {beer.description}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Container>
    </Box>
  );
};

export default PreviousBeers;
